import * as React from "react";
import { CalendarBlank, Sparkle, TextAa, WarningCircle } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

import type {
  Appointment, DailyTimelineProps, TransitionStatus,
} from "./types";
import {
  applySegment, deriveDensity, findNextActionable, groupByHour,
  isPastStatus, isSameDay, sortByStart, haptic,
} from "./utils";

import { DayHeader } from "./DayHeader";
import { DaySummary } from "./DaySummary";
import { SegmentSwitch } from "./SegmentSwitch";
import { OfflineBanner } from "./OfflineBanner";
import { HourRail } from "./HourRail";
import { NowRail } from "./NowRail";
import { TimelineCard } from "./TimelineCard";
import { NextAppointmentCta } from "./NextAppointmentCta";
import { CancelReasonSheet } from "./CancelReasonSheet";
import { NoShowConfirmSheet } from "./NoShowConfirmSheet";
import { AppointmentDetailSheet } from "./AppointmentDetailSheet";
import { SwipeActionWrapper } from "./SwipeActionWrapper";

export function DailyTimeline(props: DailyTimelineProps) {
  const {
    date, appointments, now,
    onOpenDetails, onStatusChange, onEdit, onCallCustomer,
    onDateChange,
    segment: controlledSegment, onSegmentChange,
    isLargeMode, onLargeModeToggle,
    isOffline, onAIAssist,
    isLoading, error, labels, brandOverride, dataTestId,
  } = props;

  const [internalSegment, setInternalSegment] = React.useState<DailyTimelineProps["segment"]>("all");
  const segment = controlledSegment ?? internalSegment ?? "all";
  const setSegment = (s: NonNullable<DailyTimelineProps["segment"]>) => {
    setInternalSegment(s);
    onSegmentChange?.(s);
  };

  // Brand override → CSS vars
  const brandStyle = React.useMemo<React.CSSProperties | undefined>(() => {
    if (!brandOverride) return undefined;
    const style: Record<string, string> = {};
    if (brandOverride.primaryHue) style["--biz-primary"] = brandOverride.primaryHue;
    if (brandOverride.accentHue) style["--biz-accent"] = brandOverride.accentHue;
    return style as React.CSSProperties;
  }, [brandOverride]);

  // Filter to today + sort
  const dayAppts = React.useMemo(
    () => sortByStart(appointments.filter((a) => isSameDay(a.startTime, date))),
    [appointments, date],
  );

  // The next-actionable is computed against ALL appointments (segment-agnostic)
  const nextActionable = React.useMemo(() => findNextActionable(dayAppts, now), [dayAppts, now]);

  // Apply segment for visible list
  const visible = React.useMemo(() => applySegment(dayAppts, segment, now), [dayAppts, segment, now]);

  // Density map
  const densityMap = React.useMemo(() => {
    const map = new Map<string, "past" | "active" | "upcoming">();
    for (const a of visible) {
      const isNearest = nextActionable?.id === a.id;
      map.set(a.id, deriveDensity(a, now, isNearest));
    }
    return map;
  }, [visible, now, nextActionable]);

  // Summary counts (across all day, not segment)
  const summary = React.useMemo(() => {
    let done = 0, active = 0, remaining = 0;
    for (const a of dayAppts) {
      if (isPastStatus(a.status) || a.endTime.getTime() <= now.getTime()) done++;
      else if (a.status === "in_progress") active++;
      else remaining++;
    }
    return { done, active, remaining };
  }, [dayAppts, now]);

  // Sticky "Sonraki" scroll target
  const cardRefs = React.useRef<Map<string, HTMLButtonElement | null>>(new Map());
  const nowRailRef = React.useRef<HTMLDivElement>(null);

  // Initial scroll: align "ŞİMDİ" rail to ~30% from top
  const containerRef = React.useRef<HTMLDivElement>(null);
  const didInitialScroll = React.useRef(false);
  React.useEffect(() => {
    if (didInitialScroll.current || isLoading) return;
    if (!nowRailRef.current || !containerRef.current) return;
    const c = containerRef.current;
    const target = nowRailRef.current;
    const offset = target.offsetTop - c.clientHeight * 0.3;
    c.scrollTo({ top: Math.max(0, offset), behavior: "auto" });
    didInitialScroll.current = true;
  }, [isLoading, visible.length]);

  // Sheets
  const [detailFor, setDetailFor] = React.useState<Appointment | null>(null);
  const [cancelFor, setCancelFor] = React.useState<Appointment | null>(null);
  const [noShowFor, setNoShowFor] = React.useState<Appointment | null>(null);

  const handleTap = (a: Appointment) => {
    setDetailFor(a);
    onOpenDetails?.(a);
  };

  const handleStatusChange = async (id: string, next: TransitionStatus, reason?: string) => {
    try {
      await onStatusChange?.(id, next, reason);
    } catch {
      haptic("error");
      toast.error(labels.errorTitle);
    }
  };

  const onSwipeRight = (a: Appointment) => {
    if (a.status === "in_progress") {
      void handleStatusChange(a.id, "completed");
    } else if (a.status === "confirmed" || a.status === "pending") {
      void handleStatusChange(a.id, "in_progress");
    }
  };
  const onSwipeLeft = (a: Appointment) => {
    if (a.status === "in_progress") setCancelFor(a);
    else if (a.status === "confirmed" || a.status === "pending") setNoShowFor(a);
  };

  const scrollToNext = () => {
    if (!nextActionable) return;
    const node = cardRefs.current.get(nextActionable.id);
    if (!node || !containerRef.current) return;
    const c = containerRef.current;
    const offset = node.offsetTop - 16;
    c.scrollTo({ top: Math.max(0, offset), behavior: "smooth" });
    haptic("select");
  };

  // Build hour-grouped render plan, with NOW rail injected
  const grouped = React.useMemo(() => groupByHour(visible), [visible]);
  const hours = Array.from(grouped.keys());
  const nowHour = isSameDay(now, date) ? now.getHours() : -1;

  // Keyboard 'N' = Sonraki (desktop)
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === "n" || e.key === "N") {
        e.preventDefault();
        scrollToNext();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [nextActionable]);

  return (
    <div
      data-testid={dataTestId ?? "daily-timeline"}
      style={brandStyle}
      className="h-full bg-biz-bg text-biz-foreground flex justify-center"
    >
      {/* Centered column, max 560px on bigger screens */}
      <div className="w-full max-w-[560px] flex flex-col h-full bg-biz-bg">
        <DayHeader
          date={date}
          now={now}
          labels={labels}
          onDateChange={onDateChange}
          isLargeMode={isLargeMode}
        />
        <DaySummary {...summary} labels={labels} />
        <OfflineBanner visible={!!isOffline} message={labels.offlineBanner} />
        <SegmentSwitch value={segment} onChange={setSegment} labels={labels} />

        {/* Optional toolbar: large mode + AI */}
        <div className="px-3 py-2 flex items-center gap-2 border-b border-biz-border">
          <button
            type="button"
            onClick={() => onLargeModeToggle?.(!isLargeMode)}
            aria-pressed={!!isLargeMode}
            aria-label={isLargeMode ? labels.largeModeOn : labels.largeModeOff}
            className={cn(
              "inline-flex items-center justify-center gap-1.5 h-12 px-3 rounded-biz-btn text-xs font-semibold transition-colors biz-focus-ring biz-motion-safe",
              isLargeMode
                ? "bg-biz-primary-subtle text-biz-primary-subtle-fg"
                : "border border-biz-border text-biz-foreground-muted hover:bg-biz-muted",
            )}
          >
            <TextAa size={16} weight="regular" />
            Aa
          </button>
          {onAIAssist && (
            <button
              type="button"
              onClick={() => onAIAssist("timeline")}
              className="inline-flex items-center gap-1.5 h-12 px-3 rounded-biz-btn text-xs font-semibold border border-biz-border text-biz-foreground-muted hover:bg-biz-muted transition-colors biz-focus-ring biz-motion-safe ml-auto"
            >
              <Sparkle size={16} weight="regular" />
              AI
            </button>
          )}
        </div>

        {/* Scroll area */}
        <div
          ref={containerRef}
          className="flex-1 overflow-y-auto biz-scrollbar"
          role="feed"
          aria-busy={isLoading || undefined}
          aria-label={labels.title}
        >
          {isLoading && (
            <div className="p-3 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-[120px] w-full rounded-biz-card" />
              ))}
            </div>
          )}

          {!isLoading && error && (
            <div className="p-6 text-center">
              <WarningCircle size={32} weight="regular" className="mx-auto text-biz-danger" />
              <h3 className="font-display font-semibold text-biz-foreground mt-2">{labels.errorTitle}</h3>
              <p className="text-sm text-biz-foreground-muted mt-1">{error}</p>
              <button
                type="button"
                onClick={() => onDateChange?.(new Date(date))}
                className="mt-4 inline-flex items-center justify-center h-12 px-4 rounded-biz-btn bg-biz-primary text-biz-primary-foreground text-sm font-semibold biz-focus-ring biz-motion-safe"
              >
                {labels.errorRetry}
              </button>
            </div>
          )}

          {!isLoading && !error && visible.length === 0 && (
            <div className="p-10 text-center">
              <CalendarBlank size={40} weight="regular" className="mx-auto text-biz-foreground-subtle" />
              <p className="mt-3 text-sm text-biz-foreground-muted">{labels.empty}</p>
            </div>
          )}

          {!isLoading && !error && visible.length > 0 && (
            <div className="px-3 pb-3 space-y-2">
              {hours.map((h) => {
                const items = grouped.get(h)!;
                const showNowAfter = nowHour === h;
                return (
                  <React.Fragment key={h}>
                    <HourRail hour={h} />
                    {items.map((a) => {
                      const density = densityMap.get(a.id) ?? "upcoming";
                      const card = (
                        <TimelineCard
                          ref={(node) => {
                            if (node) cardRefs.current.set(a.id, node);
                            else cardRefs.current.delete(a.id);
                          }}
                          appointment={a}
                          density={density}
                          now={now}
                          labels={labels}
                          isLargeMode={isLargeMode}
                          onTap={handleTap}
                          onCall={(p) => onCallCustomer?.(p)}
                          onMenu={(ap) => setDetailFor(ap)}
                        />
                      );
                      const enabled = density !== "past";
                      return (
                        <SwipeActionWrapper
                          key={a.id}
                          enabled={enabled}
                          rightLabel={
                            a.status === "in_progress" ? labels.completeAction : labels.markInProgressAction
                          }
                          leftLabel={
                            a.status === "in_progress" ? labels.cancelAction : labels.markNoShowAction
                          }
                          rightTone={a.status === "in_progress" ? "success" : "info"}
                          leftTone={a.status === "in_progress" ? "danger" : "warning"}
                          onSwipeRight={() => onSwipeRight(a)}
                          onSwipeLeft={() => onSwipeLeft(a)}
                        >
                          {card}
                        </SwipeActionWrapper>
                      );
                    })}
                    {showNowAfter && <NowRail ref={nowRailRef} label={labels.nowMarker} />}
                  </React.Fragment>
                );
              })}
              {/* If now is after every hour group, still render the rail at end */}
              {nowHour > 0 && hours.length > 0 && nowHour > hours[hours.length - 1] && (
                <NowRail ref={nowRailRef} label={labels.nowMarker} />
              )}
            </div>
          )}
        </div>

        <NextAppointmentCta
          next={nextActionable}
          now={now}
          labels={labels}
          onClick={scrollToNext}
        />
      </div>

      {/* Sheets */}
      <AppointmentDetailSheet
        appointment={detailFor}
        open={!!detailFor}
        labels={labels}
        onClose={() => setDetailFor(null)}
        onEdit={(a) => { setDetailFor(null); onEdit?.(a); }}
        onCallCustomer={onCallCustomer}
        onAction={(a, next) => {
          setDetailFor(null);
          if (next === "cancelled") setCancelFor(a);
          else if (next === "no_show") setNoShowFor(a);
          else void handleStatusChange(a.id, next);
        }}
      />

      <CancelReasonSheet
        open={!!cancelFor}
        labels={labels}
        onClose={() => setCancelFor(null)}
        onConfirm={(reason) => {
          if (cancelFor) void handleStatusChange(cancelFor.id, "cancelled", reason);
          setCancelFor(null);
        }}
      />

      <NoShowConfirmSheet
        open={!!noShowFor}
        labels={labels}
        onClose={() => setNoShowFor(null)}
        onConfirm={() => {
          if (noShowFor) void handleStatusChange(noShowFor.id, "no_show");
          setNoShowFor(null);
        }}
      />
    </div>
  );
}

export default DailyTimeline;
